import { useEffect, useRef, useCallback } from 'react'

interface PerformanceMetrics {
  fcp: number | null // First Contentful Paint
  lcp: number | null // Largest Contentful Paint
  fid: number | null // First Input Delay
  cls: number | null // Cumulative Layout Shift
  ttfb: number | null // Time to First Byte
  domLoad: number | null // DOM Content Loaded
  windowLoad: number | null // Window Load
}

interface UserInteraction {
  type: 'click' | 'scroll' | 'navigation' | 'search' | 'cart_action'
  target: string
  timestamp: number
  data?: Record<string, unknown>
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics = {
    fcp: null,
    lcp: null,
    fid: null,
    cls: null,
    ttfb: null,
    domLoad: null,
    windowLoad: null,
  }

  private interactions: UserInteraction[] = []
  private observers: PerformanceObserver[] = []

  constructor() {
    this.initializeObservers()
    this.captureInitialMetrics()
  }

  private initializeObservers() {
    // First Contentful Paint
    if ('PerformanceObserver' in window) {
      try {
        const fcpObserver = new PerformanceObserver(list => {
          const entries = list.getEntries()
          const fcpEntry = entries.find(
            entry => entry.name === 'first-contentful-paint'
          )
          if (fcpEntry) {
            this.metrics.fcp = fcpEntry.startTime
            this.logMetric('FCP', fcpEntry.startTime)
          }
        })
        fcpObserver.observe({ entryTypes: ['paint'] })
        this.observers.push(fcpObserver)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('FCP observer failed:', error)
      }

      // Largest Contentful Paint
      try {
        const lcpObserver = new PerformanceObserver(list => {
          const entries = list.getEntries()
          const lastEntry = entries[entries.length - 1]
          if (lastEntry) {
            this.metrics.lcp = lastEntry.startTime
            this.logMetric('LCP', lastEntry.startTime)
          }
        })
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })
        this.observers.push(lcpObserver)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('LCP observer failed:', error)
      }

      // First Input Delay
      try {
        const fidObserver = new PerformanceObserver(list => {
          const entries = list.getEntries()
          entries.forEach(entry => {
            this.metrics.fid =
              (entry as PerformanceEventTiming).processingStart -
              entry.startTime
            this.logMetric('FID', this.metrics.fid)
          })
        })
        fidObserver.observe({ entryTypes: ['first-input'] })
        this.observers.push(fidObserver)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('FID observer failed:', error)
      }

      // Cumulative Layout Shift
      try {
        const clsObserver = new PerformanceObserver(list => {
          let clsValue = 0
          list.getEntries().forEach(
            (
              entry: PerformanceEntry & {
                hadRecentInput?: boolean
                value?: number
              }
            ) => {
              if (!entry.hadRecentInput) {
                clsValue += entry.value || 0
              }
            }
          )
          this.metrics.cls = clsValue
          this.logMetric('CLS', clsValue)
        })
        clsObserver.observe({ entryTypes: ['layout-shift'] })
        this.observers.push(clsObserver)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('CLS observer failed:', error)
      }
    }
  }

  private captureInitialMetrics() {
    // Time to First Byte
    const navigationEntry = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming
    if (navigationEntry) {
      this.metrics.ttfb =
        navigationEntry.responseStart - navigationEntry.requestStart
      this.logMetric('TTFB', this.metrics.ttfb)
    }

    // DOM Content Loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.metrics.domLoad = performance.now()
        this.logMetric('DOM Load', this.metrics.domLoad)
      })
    } else {
      this.metrics.domLoad = performance.now()
      this.logMetric('DOM Load', this.metrics.domLoad)
    }

    // Window Load
    if (document.readyState === 'complete') {
      this.metrics.windowLoad = performance.now()
      this.logMetric('Window Load', this.metrics.windowLoad)
    } else {
      window.addEventListener('load', () => {
        this.metrics.windowLoad = performance.now()
        this.logMetric('Window Load', this.metrics.windowLoad)
      })
    }
  }

  private logMetric(name: string, value: number) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`Performance Metric - ${name}:`, value.toFixed(2), 'ms')
    }
  }

  trackInteraction(interaction: UserInteraction) {
    this.interactions.push(interaction)

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('User Interaction:', interaction)
    }

    // Send to analytics in production
    if (import.meta.env.PROD) {
      this.sendToAnalytics('interaction', interaction)
    }
  }

  getMetrics(): PerformanceMetrics {
    return { ...this.metrics }
  }

  getInteractions(): UserInteraction[] {
    return [...this.interactions]
  }

  private sendToAnalytics(type: string, data: unknown) {
    // In a real app, you would send this to Google Analytics, Mixpanel, etc.
    fetch('/api/analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type,
        data,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      }),
    }).catch(() => {
      // Silently handle analytics failures
    })
  }

  destroy() {
    this.observers.forEach(observer => observer.disconnect())
  }
}

// Global performance monitor instance
const performanceMonitor = new PerformanceMonitor()

export const usePerformance = () => {
  const isInitialized = useRef(false)

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true

      // Track page load performance
      const pageLoadTime = performance.now()
      performanceMonitor.trackInteraction({
        type: 'navigation',
        target: window.location.pathname,
        timestamp: pageLoadTime,
        data: { loadTime: pageLoadTime },
      })
    }

    return () => {
      // Cleanup on unmount
      performanceMonitor.destroy()
    }
  }, [])

  const trackClick = useCallback(
    (target: string, data?: Record<string, unknown>) => {
      performanceMonitor.trackInteraction({
        type: 'click',
        target,
        timestamp: performance.now(),
        data,
      })
    },
    []
  )

  const trackScroll = useCallback((target: string, scrollDepth: number) => {
    performanceMonitor.trackInteraction({
      type: 'scroll',
      target,
      timestamp: performance.now(),
      data: { scrollDepth },
    })
  }, [])

  const trackSearch = useCallback((query: string, resultsCount?: number) => {
    performanceMonitor.trackInteraction({
      type: 'search',
      target: 'search',
      timestamp: performance.now(),
      data: { query, resultsCount },
    })
  }, [])

  const trackCartAction = useCallback(
    (action: string, itemId?: string, quantity?: number) => {
      performanceMonitor.trackInteraction({
        type: 'cart_action',
        target: 'cart',
        timestamp: performance.now(),
        data: { action, itemId, quantity },
      })
    },
    []
  )

  const getMetrics = useCallback(() => {
    return performanceMonitor.getMetrics()
  }, [])

  const getInteractions = useCallback(() => {
    return performanceMonitor.getInteractions()
  }, [])

  return {
    trackClick,
    trackScroll,
    trackSearch,
    trackCartAction,
    getMetrics,
    getInteractions,
  }
}
